import type { Component } from 'vue'
import { LogIn } from '@lucide/vue'

// Import PNG icons for tarot tabs
import historyIcon from '@app/assets/images/icons/history_icon.png'
import learnIcon from '@app/assets/images/icons/learn_icon.png'
import spreadsIcon from '@app/assets/images/icons/spreads_icon.png'
import chatIcon from '@app/assets/images/icons/chat_icon.png'
import profileIcon from '@app/assets/images/icons/profile_cat_icon.png'

/**
 * Access levels for navigation items:
 * - 'public': Visible to everyone (guests and authenticated users)
 * - 'guest': Visible only to guests (not logged in)
 * - 'auth': Visible only to authenticated users
 * - 'admin': Visible only to admin or owner users
 * - 'owner': Visible only to owner users (highest privilege)
 */
export type AccessLevel = 'public' | 'guest' | 'auth' | 'admin' | 'owner'

export interface NavItem {
  id: string
  href: string
  icon?: Component  // @lucide/vue icon component
  iconUrl?: string  // PNG/image URL for custom icons
  labelKey: string // i18n key for translation
  access: AccessLevel
  /** Show in mobile bottom tabs (max 5 recommended) */
  showInMobile?: boolean
  /** Show in desktop header nav */
  showInDesktop?: boolean
  /** Show lock icon for non-authenticated users (only for public items) */
  lockedForGuests?: boolean
}

/**
 * Main app navigation items for Tarot app
 * Order: history, learn, spread (home), chat, profile
 */
export const navItems: NavItem[] = [
  {
    id: 'history',
    href: '/history',
    iconUrl: historyIcon,
    labelKey: 'nav.history',
    access: 'public',
    showInMobile: true,
    showInDesktop: true,
  },
  {
    id: 'learn',
    href: '/learn',
    iconUrl: learnIcon,
    labelKey: 'nav.learn',
    access: 'public',
    showInMobile: true,
    showInDesktop: true,
  },
  {
    id: 'spread',
    href: '/spread',
    iconUrl: spreadsIcon,
    labelKey: 'nav.home',
    access: 'public',
    showInMobile: true,
    showInDesktop: true,
  },
  {
    id: 'chat',
    href: '/chat',
    iconUrl: chatIcon,
    labelKey: 'nav.chat',
    access: 'public',
    showInMobile: true,
    showInDesktop: true,
  },
  {
    id: 'profile',
    href: '/profile',
    iconUrl: profileIcon,
    labelKey: 'nav.profile',
    access: 'public',
    showInMobile: true,
    showInDesktop: true,
    lockedForGuests: true,
  },
]

/**
 * Auth CTA item - shown to web guests who aren't logged in
 */
export const authCtaItem: NavItem | null = {
  id: 'login',
  href: '/login',
  icon: LogIn,
  labelKey: 'nav.login',
  access: 'guest',
  showInMobile: true,
  showInDesktop: true,
}

/**
 * Filter nav items based on user access level
 */
export function filterNavItems(
  items: NavItem[],
  options: {
    isAuthenticated: boolean
    isAdmin?: boolean
    isOwner?: boolean
    mobileOnly?: boolean
    desktopOnly?: boolean
  }
): NavItem[] {
  const { isAuthenticated, isAdmin = false, isOwner = false, mobileOnly, desktopOnly } = options

  return items.filter((item) => {
    // Check access level
    const hasAccess = (() => {
      switch (item.access) {
        case 'public':
          return true
        case 'guest':
          return !isAuthenticated
        case 'auth':
          return isAuthenticated
        case 'admin':
          return isAdmin || isOwner // admin OR owner can access
        case 'owner':
          return isOwner // only owner
        default:
          return false
      }
    })()

    if (!hasAccess) return false

    // Check display context
    if (mobileOnly && !item.showInMobile) return false
    if (desktopOnly && !item.showInDesktop) return false

    return true
  })
}

/**
 * Get all visible nav items for current user state
 */
export function getVisibleNavItems(options: {
  isAuthenticated: boolean
  isAdmin?: boolean
  isOwner?: boolean
}): {
  mainNav: NavItem[]
  mobileNav: NavItem[]
  showLoginCta: boolean
} {
  const { isAuthenticated, isAdmin, isOwner } = options

  const mainNav = filterNavItems(navItems, {
    isAuthenticated,
    isAdmin,
    isOwner,
    desktopOnly: true,
  })

  const mobileNav = filterNavItems(navItems, {
    isAuthenticated,
    isAdmin,
    isOwner,
    mobileOnly: true,
  })

  const showLoginCta = !isAuthenticated

  return { mainNav, mobileNav, showLoginCta }
}
